// React, Next
import { ReactElement, useEffect, useRef, useState } from 'react'

// MUI
import Box from '@mui/material/Box'
import { motion } from 'framer-motion'

// Sections
import HeroSection2 from './sections/HeroSection2'
import IntroSection from './sections/IntroSection'
import OtSecuritySection from './sections/OtSecuritySection'
import IndustrialIotSection from './sections/IndustrialIotSection'
import MonitoringSection from './sections/MonitoringSection'
import StackSection from './sections/StackSection'
import WhyUsSection from './sections/WhyUsSection'
import SimpleSection from './sections/SimpleSection'
import CtaSection from './sections/CtaSection'

// Style
import themeColors from '@/assets/theme/colors'

const SECTION_IDS = [
  'intro',
  'otSecurity',
  'industrialIot',
  'monitoring',
  'stack',
  'whyUs',
  'simple',
  'cta'
] as const

type SectionId = (typeof SECTION_IDS)[number]

export default function IndexView() {
  const refs = useRef<Record<string, HTMLDivElement | null>>({})
  const [visible, setVisible] = useState<Record<string, boolean>>({})

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const id = entry.target.getAttribute('data-section')
          if (!id) return
          setVisible(prev => ({ ...prev, [id]: entry.isIntersecting }))
        })
      },
      { threshold: 0.1 }
    )

    SECTION_IDS.forEach(id => {
      const node = refs.current[id]
      if (node) observer.observe(node)
    })

    return () => observer.disconnect()
  }, [])

  /** Each band fades up once it comes into view. */
  const getSection = (id: SectionId, section: ReactElement) => (
    <motion.div
      key={id}
      ref={node => {
        refs.current[id] = node
      }}
      data-section={id}
      initial={{ opacity: 0, y: 20 }}
      animate={visible[id] ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ delay: 0.07, duration: 0.75 }}
    >
      {section}
    </motion.div>
  )

  // *** MAIN RENDER ***
  return (
    <Box sx={{ background: themeColors.kroemtech.background }}>
      <HeroSection2 />

      {getSection('intro', <IntroSection />)}
      {getSection('otSecurity', <OtSecuritySection />)}
      {getSection('industrialIot', <IndustrialIotSection />)}
      {getSection('monitoring', <MonitoringSection />)}
      {getSection('stack', <StackSection />)}
      {getSection('whyUs', <WhyUsSection />)}
      {getSection('simple', <SimpleSection />)}
      {getSection('cta', <CtaSection />)}
    </Box>
  )
}
